// A public tracking URL already contains the shipment id and its share token.  The only state
// that must survive the Keycloak round trip is the user's explicit "save this" choice.
//
// It lives in localStorage, not sessionStorage: registration ends with an email confirmation
// link, and that link is opened in another tab (webmail, or a mail app handing off to the
// browser). The return address survives that hop via postAuthRedirect; with a tab-scoped intent
// the user came back to the right page and nothing happened.
//
// Shared across tabs, the intent is kept narrow instead:
// - it only ever fires on the exact shipment id + token it was created for, so the worst a stale
//   one can do is save a link the user opened again and already asked to save;
// - it expires after INTENT_TTL_MS (a registration + email round trip fits comfortably), and an
//   expired, malformed or future-dated record is deleted on read, so the share token does not
//   linger on a shared computer;
// - it is removed as soon as the save succeeds.
const PUBLIC_TRACK_SAVE_INTENT_KEY = 'lupa.publicTrackSaveIntent.v2'
export const INTENT_TTL_MS = 60 * 60 * 1000
// Tolerates a small clock adjustment; anything further in the future was not written by us.
const CLOCK_SKEW_MS = 5 * 60 * 1000

type StoredPublicTrackSaveIntent = {
  version: 2
  shipmentId: string
  token: string
  createdAt: number
  // A failed automatic request must not turn each React re-render or page reload into another
  // POST. Consent is the one exception: accepting it reloads the current page, so that branch
  // explicitly re-arms the intent.
  autoSaveAttempted: boolean
}

export type PublicTrackSaveIntent = Readonly<StoredPublicTrackSaveIntent>

function getStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    // Private browsing or a full quota must not prevent a person from opening the public link.
    return null
  }
}

function isValidIntent(value: unknown, now: number): value is StoredPublicTrackSaveIntent {
  if (!value || typeof value !== 'object') return false
  const v = value as StoredPublicTrackSaveIntent
  return (
    v.version === 2 &&
    typeof v.shipmentId === 'string' && v.shipmentId !== '' &&
    typeof v.token === 'string' && v.token !== '' &&
    typeof v.autoSaveAttempted === 'boolean' &&
    typeof v.createdAt === 'number' && Number.isFinite(v.createdAt) &&
    v.createdAt <= now + CLOCK_SKEW_MS &&
    now - v.createdAt < INTENT_TTL_MS
  )
}

function readIntent(): StoredPublicTrackSaveIntent | null {
  const storage = getStorage()
  if (!storage) return null

  try {
    const raw = storage.getItem(PUBLIC_TRACK_SAVE_INTENT_KEY)
    if (!raw) return null
    const value: unknown = JSON.parse(raw)
    if (!isValidIntent(value, Date.now())) {
      storage.removeItem(PUBLIC_TRACK_SAVE_INTENT_KEY)
      return null
    }
    return value
  } catch {
    try {
      storage.removeItem(PUBLIC_TRACK_SAVE_INTENT_KEY)
    } catch {
      // Nothing more to do; an unreadable record never matches a link.
    }
    return null
  }
}

function writeIntent(intent: StoredPublicTrackSaveIntent): void {
  try {
    getStorage()?.setItem(PUBLIC_TRACK_SAVE_INTENT_KEY, JSON.stringify(intent))
  } catch {
    // The manual save button remains available after login when storage is unavailable.
  }
}

function matches(intent: StoredPublicTrackSaveIntent | null, shipmentId: string, token: string): intent is StoredPublicTrackSaveIntent {
  return Boolean(intent && intent.shipmentId === shipmentId && intent.token === token)
}

/** Records the guest's explicit request to save exactly this public tracking link. */
export function rememberPublicTrackSaveIntent(shipmentId: string, token: string): void {
  if (!shipmentId || !token) return
  writeIntent({ version: 2, shipmentId, token, createdAt: Date.now(), autoSaveAttempted: false })
}

/** Returns a pending intent only when the current public URL is the one that created it. */
export function getPublicTrackSaveIntent(shipmentId: string, token: string): PublicTrackSaveIntent | null {
  const intent = readIntent()
  return matches(intent, shipmentId, token) ? intent : null
}

export function hasPublicTrackSaveIntentForLocation(pathname: string, search: string): boolean {
  const match = pathname.match(/^\/track\/([^/]+)\/?$/)
  const token = new URLSearchParams(search).get('token') ?? ''
  return Boolean(match && token && getPublicTrackSaveIntent(match[1], token))
}

/**
 * Marks the one automatic POST attempt before it starts. A manual attempt marks the same flag
 * too: otherwise its error would make the next render mistake that manual intent for a fresh
 * automatic request.
 */
export function markPublicTrackSaveAutoAttempted(shipmentId: string, token: string): void {
  const intent = readIntent()
  if (!matches(intent, shipmentId, token)) return
  writeIntent({ ...intent, autoSaveAttempted: true })
}

/** Consent is an intentional interruption, not a save failure; allow the post-consent retry. */
export function rearmPublicTrackSaveAutoAttempt(shipmentId: string, token: string): void {
  const intent = readIntent()
  if (!matches(intent, shipmentId, token)) return
  writeIntent({ ...intent, autoSaveAttempted: false })
}

/** Clears only the exact intent that was successfully saved, never another tab's newer choice. */
export function clearPublicTrackSaveIntent(shipmentId: string, token: string): void {
  const intent = readIntent()
  if (!matches(intent, shipmentId, token)) return
  try {
    getStorage()?.removeItem(PUBLIC_TRACK_SAVE_INTENT_KEY)
  } catch {
    // A successful API response is still authoritative; this only leaves a harmless stale hint.
  }
}
